import { useState, useEffect, useCallback } from 'react';
import { Box, Card, Button, Chip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from 'components/Layout/Spinner';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';
import { usePaginationPreference } from 'hooks/usePaginationPreference';
import { useDialog } from 'common/DialogProvider';
import { HRApiWrapper } from 'api/HRApiWrapper';
import JobOpeningDrawer from './JobOpeningDrawer';
import JobOpeningDetail from './JobOpeningDetail';
import ApplicationsDialog from './ApplicationsDialog';

const JobOpeningsPage: React.FC = () => {
  const { employeeApi }: Api = useApi();
  const hrApi = new HRApiWrapper(employeeApi);
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();

  const [items, setItems] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [seniorities, setSeniorities] = useState<any[]>([]);
  const [educationLevels, setEducationLevels] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [applicationsOpen, setApplicationsOpen] = useState(false);
  const [applicationsJobOpening, setApplicationsJobOpening] = useState<any>(null);

  const reloadSelected = useCallback(async (id: number) => {
    try {
      const res = await hrApi.getJobOpeningById('1', id);
      const item = res.data?.data ?? res.data;
      setSelected(item);
      return item;
    } catch (error) {
      handleAPIError(error, toast, errors);
    }
  }, []);
  const [errors] = useState({});
  const [paginationModel, setPaginationModel] = usePaginationPreference('hrJobOpenings', 25);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, skillsRes, senRes, eduRes] = await Promise.all([
        hrApi.getJobOpenings('1', 1, 1000),
        hrApi.getSkills('1', 1, 1000),
        hrApi.getSeniorities(),
        hrApi.getEducationLevels(),
      ]);
      setItems(jobsRes.data?.data?.queryResult ?? jobsRes.data?.data ?? []);
      setSkills(skillsRes.data?.data?.queryResult ?? skillsRes.data?.data ?? []);
      setSeniorities(senRes.data?.data ?? senRes.data ?? []);
      setEducationLevels(eduRes.data?.data ?? eduRes.data ?? []);
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, []);

  const handleDelete = (id: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.roleRow.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          try { await hrApi.deleteJobOpening('1', id); fetchAll(); }
          catch (error) { handleAPIError(error, toast, errors); }
          finally { closeDialog(dialogId); }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => closeDialog(dialogId),
      },
    });
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'title', headerName: intl.get('hr.jobOpening.title'), flex: 1 },
    { field: 'seniority', headerName: intl.get('hr.jobOpening.seniority'), width: 160, valueGetter: (_: any, row: any) => row.seniority?.name ?? '' },
    {
      field: 'requiredExperience', headerName: intl.get('hr.jobOpening.experience'), width: 130,
      renderCell: (params) => params.value ? `${params.value}y` : '',
    },
    {
      field: 'requiredSkills', headerName: intl.get('hr.jobOpening.requiredSkills'), width: 200,
      renderCell: (params) => {
        const required = (params.row.jobOpeningSkills ?? []).filter((s: any) => s.required);
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', height: '100%' }}>
            {required.slice(0, 3).map((s: any) => <Chip key={s.id} label={s.skill?.name} size="small" color="primary" variant="outlined" />)}
            {required.length > 3 && <Chip label={`+${required.length - 3}`} size="small" />}
          </Box>
        );
      },
    },
    {
      field: 'applications', headerName: intl.get('hr.jobOpening.applications'), width: 130,
      renderCell: (params) => (
        <Chip
          label={(params.row.applications ?? []).length}
          size="small"
          clickable
          color={(params.row.applications ?? []).length > 0 ? 'primary' : 'default'}
          onClick={() => { setApplicationsJobOpening(params.row); setApplicationsOpen(true); }}
        />
      ),
    },
    {
      field: 'actions', headerName: '', width: 140, sortable: false,
      renderCell: (params) => (
        <DefaultActions row={params.row} entityType={ENTITY_TYPE.EMPLOYEE}
          onEdit={async () => { await reloadSelected(params.row.id); setDetailOpen(true); }}
          onDelete={() => handleDelete(params.row.id)}
          editScopes={['recruitment.manage']} deleteScopes={['recruitment.manage']} />
      ),
    },
  ];

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 3 }}>
          {intl.get('mainNavigation.humanResources.jobOpenings')}
        </Typography>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelected(null); setDrawerOpen(true); }}>
            {intl.get('common.add')}
          </Button>
        </Box>
        {loading ? <Spinner /> : (
          <Box style={{ width: '100%', display: 'flex' }}>
            <DataGrid
              rows={items}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              sortingMode="client"
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Box>

      <JobOpeningDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={async (data) => { await hrApi.createJobOpening('1', data); setDrawerOpen(false); fetchAll(); }}
        item={null}
        seniorities={seniorities}
        educationLevels={educationLevels}
      />

      {selected && (
        <JobOpeningDetail
          open={detailOpen}
          onClose={() => { setDetailOpen(false); fetchAll(); }}
          onReload={() => reloadSelected(selected.id)}
          jobOpening={selected}
          hrApi={hrApi}
          skills={skills}
          seniorities={seniorities}
          educationLevels={educationLevels}
        />
      )}

      {applicationsJobOpening && (
        <ApplicationsDialog
          open={applicationsOpen}
          onClose={() => setApplicationsOpen(false)}
          jobOpening={applicationsJobOpening}
          hrApi={hrApi}
        />
      )}
    </Card>
  );
};

export default JobOpeningsPage;
